import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { ShoppingCart, Pencil, Undo2, FilePlus2, ReceiptText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SaleEditor } from "@/components/SaleEditor";
import { ModuleHeader, SaleCard } from "@/components/SaleCard";

const TABS = [{ k: "new", label: "Yeni Satış", icon: FilePlus2 }, { k: "mine", label: "Fişlerim", icon: ReceiptText }];

function MySales({ sales, reload }) {
  const [edit, setEdit] = useState(null);
  const withdraw = async (s) => {
    if (!window.confirm("Fiş geri çekilsin mi? Muhasebe listesinden silinir.")) return;
    try { await api.delete(`/sales/${s.id}`); toast.success("Fiş geri çekildi"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const save = async (body) => { await api.put(`/sales/${edit.id}`, body); toast.success("Fiş güncellendi"); setEdit(null); reload(); };
  if (!sales.length) return <div className="text-sm text-muted-foreground p-6 text-center rounded-xl border border-dashed border-border">Henüz fiş yok.</div>;
  return (
    <div className="space-y-4" data-testid="my-sales">
      {sales.map((s, i) => (
        <SaleCard key={s.id} sale={s} testid={`my-sale-${i}`}>
          {s.status === "pending" && (
            <>
              <button onClick={() => setEdit(s)} data-testid={`my-sale-edit-${i}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-secondary text-sm font-semibold hover:bg-secondary/70 transition-colors"><Pencil size={15} /> Düzenle</button>
              <button onClick={() => withdraw(s)} data-testid={`my-sale-withdraw-${i}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-destructive/10 text-destructive text-sm font-semibold hover:bg-destructive/20 transition-colors"><Undo2 size={15} /> Geri Çek</button>
            </>
          )}
        </SaleCard>
      ))}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="my-sale-edit-dialog">
          <DialogHeader><DialogTitle className="font-head">Fişi Düzenle</DialogTitle></DialogHeader>
          {edit && <SaleEditor initial={edit} submitLabel="Değişiklikleri Kaydet" onSubmit={save} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Sales({ onBack }) {
  const [tab, setTab] = useState("new");
  const [sales, setSales] = useState([]);
  const load = useCallback(() => { api.get("/sales", { params: { mine: true } }).then((r) => setSales(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const create = async (body) => {
    const r = await api.post("/sales", body);
    toast.success(r.data?._offline ? "Çevrimdışı — fiş kuyruğa alındı, internet gelince gönderilecek" : "Fiş muhasebeye gönderildi (Onay Bekliyor)");
    load();
  };
  return (
    <div className="min-h-screen bg-background" data-testid="sales-module">
      <ModuleHeader icon={ShoppingCart} title="Pazarlama / Satış" onBack={onBack} testid="sales" />
      <div className="p-4 max-w-2xl mx-auto">
        <div className="flex gap-2 mb-5">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`sales-tab-${t.k}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              <t.icon size={15} /> {t.label}{t.k === "mine" && sales.filter((s) => s.status === "pending").length > 0 && <span className="ml-1 text-[11px] opacity-80">({sales.filter((s) => s.status === "pending").length})</span>}
            </button>
          ))}
        </div>
        {tab === "new" ? <SaleEditor onSubmit={create} /> : <MySales sales={sales} reload={load} />}
      </div>
    </div>
  );
}
